/* eslint-disable lit-a11y/mouse-events-have-key-events */
/* eslint-disable lit-a11y/click-events-have-key-events */
import { html, LitElement } from 'lit-element';
import { ScopedElementsMixin } from '@open-wc/scoped-elements';

import AabIcon from '@aab/sc-aab-icon/src/AabIcon';

import convertOptions from './lib/convertOptions';
import KeyboardDispatcherMixin from './lib/KeyboardDispatcherMixin';
import {
  findFirstGroupedOption,
  findLastGroupedOption,
  findFirstOption,
  findLastOption,
  findNextOption,
  findPreviousOption,
  findNextGroupedOption,
  findPreviousGroupedOption,
} from './lib/findOption';

import AabSelectButton from './components/AabSelectButton';
import AabSelectDropdown from './components/AabSelectDropdown';
import AabSelectGroupedDropdown from './components/AabSelectGroupedDropdown';

import styles from './styles.scss';

/**
 * @typedef {object} Option
 * @property {string} name
 * @property {string} value
 * @property {?string} selectedName - can be used when the (displayed) name of the option should differ when selected versus how it's displayed in the dropdown
 * @property {?string} icon - an svg string which gets passed into an aab-icon, and gets in front of the option/target labels
 * @property {?boolean} disabled
 */

/**
 * @typedef {object} Group
 * @property {string} name
 * @property {Option[]} options
 */

/**
 * @typedef {LitElement} AabSelect
 * @property {Option[]} options
 * @property {Group[]} groups
 * @property {string} value
 * @property {string} variant - can be empty, 'success', 'error', 'warning'
 * @property {boolean} disabled
 * @property {boolean} noBorder
 * @property {string} placeholder
 * @property {string} label
 * @property {string} sublabel
 * @property {string} alignDropdown - can be 'left' (default) or 'right'
 */
export default class AabSelect extends KeyboardDispatcherMixin(
  ScopedElementsMixin(LitElement),
) {
  static get properties() {
    return {
      value: { type: String, reflect: true, attribute: 'aab-select-value' },
      options: {
        type: Array,
        converter: (value) => convertOptions(JSON.parse(value)),
      },
      groups: {
        type: Array,
        converter(value) {
          return JSON.parse(value).map((group) => ({
            name: group.name,
            options: convertOptions(group.options),
          }));
        },
      },
      variant: { type: String },
      disabled: { type: Boolean, attribute: 'aab-select-disabled' },
      noBorder: { type: Boolean, attribute: 'no-border' },
      placeholder: { type: String, attribute: 'aab-select-placeholder' },
      label: { type: String, attribute: 'aab-select-label' },
      sublabel: { type: String, attribute: 'aab-select-sublabel' },
      dropdownWidth: { type: String, attribute: 'dropdown-width' },
      alignDropdown: { type: String, attribute: 'align-dropdown' },
      isOpen: { type: Boolean, attribute: false },
      focusedOptionIndex: { type: Number, attribute: false },
      focusedGroupIndex: { type: Number, attribute: false },
    };
  }

  static get scopedElements() {
    return {
      'aab-icon': AabIcon,
    };
  }

  static get styles() {
    return [styles];
  }

  constructor() {
    super();
    this.options = [];
    this.groups = [];
    this.isOpen = false;
    this.focusedOptionIndex = 0;
    this.focusedGroupIndex = 0;
    this.dropdownWidth = 'auto';
    this.alignDropdown = 'left';
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('click', this.handleDocumentClick.bind(this));
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('click', this.handleDocumentClick.bind(this));
  }

  get isGroupedSelect() {
    return !!this.groups.length;
  }

  get selectedOption() {
    if (!this.value) {
      return null;
    }

    let selected = null;
    if (this.isGroupedSelect) {
      selected = this.groups.reduce((foundOption, group) => foundOption || group.options.find((option) => option.value === this.value), undefined);
    } else if (this.options) {
      selected = this.options.find(
        (option) => option.value === this.value,
      );
    }
    return selected;
  }

  get focusedOption() {
    let option;
    if (this.isGroupedSelect) {
      option = this.groups[this.focusedGroupIndex].options[this.focusedOptionIndex];
    } else {
      option = this.options[this.focusedOptionIndex];
    }
    return option;
  }

  get selectedOptionIndeces() {
    let selectedGroupIndex = -1;
    let selectedOptionIndex = -1;

    if (this.isGroupedSelect) {
      [selectedGroupIndex, selectedOptionIndex] = this.groups.reduce((result, group, index) => {
        if (result) {
          return result;
        }
        const optionIndex = group.options.findIndex((o) => o.value === this.value);
        return optionIndex > -1 ? [index, optionIndex] : null;
      }, null);
    } else {
      selectedOptionIndex = this.options.findIndex((option) => option.value === this.value);
    }

    return { selectedGroupIndex, selectedOptionIndex };
  }

  get hasAvailableOptions() {
    if (this.isGroupedSelect) {
      return this.groups.some((group) => group.options.some((option) => !option.disabled));
    }
    return this.options.some((option) => !option.disabled);
  }

  displayValue() {
    if (!this.value) {
      return this.placeholder || '-';
    }

    const { selectedOption } = this;
    if (!selectedOption) {
      return 'Invalid value';
    }
    return selectedOption.selectedName ?? selectedOption.name;
  }

  handleOptionSelected() {
    const option = this.focusedOption;
    const changeEvent = new CustomEvent('aab-select-value-change', {
      detail: {
        value: option.value,
      },
    });
    this.dispatchEvent(changeEvent);
    this.toggleDropdown();
  }

  async toggleDropdown() {
    if (this.disabled) {
      return;
    }
    this.isOpen = !this.isOpen;

    if (this.isOpen) {
      const { selectedGroupIndex, selectedOptionIndex } = this.selectedOptionIndeces;
      this.focusedGroupIndex = selectedGroupIndex > -1 ? selectedGroupIndex : 0;
      this.focusedOptionIndex = selectedOptionIndex > -1 ? selectedOptionIndex : 0;

      // Wait for the dropdown to be rendered before we focus it
      await this.updateComplete;
      this.focusDropdown();
    } else {
      this.focusTarget();
    }
  }

  focusDropdown() {
    this.shadowRoot.querySelector('.dropdown').focus();
  }

  focusTarget() {
    this.shadowRoot.querySelector('.target').focus();
  }

  /**
   * Keyboard and 'outside click' event handlers
   */
  handleDocumentClick(event) {
    // clickedOutside is defined as 'this' not being in the hierarchy of clicked elements
    const clickedOutside = !event.composedPath().find((element) => element === this);
    if (clickedOutside) {
      if (this.isOpen) {
        this.focusTarget();
      }
      this.isOpen = false;
    }
  }

  handleEnterKey(event) {
    this.handleEnterOrSpaceBar(event);
  }

  handleSpaceBarKey(event) {
    this.handleEnterOrSpaceBar(event);
  }

  handleEnterOrSpaceBar(event) {
    if (!this.isOpen) {
      this.toggleDropdown();
    } else {
      this.handleOptionSelected();
    }
    event.preventDefault();
  }

  handleTabKey(event) {
    if (this.isOpen) {
      event.preventDefault();
    }
  }

  handleEscapeKey(event) {
    this.toggleDropdown();
    event.preventDefault();
  }

  handleHomeKey(event) {
    if (this.isGroupedSelect) {
      const { groupIndex, optionIndex } = findFirstGroupedOption(this.groups);
      this.focusedGroupIndex = groupIndex;
      this.focusedOptionIndex = optionIndex;
    } else {
      const { index } = findFirstOption(this.options);
      this.focusedOptionIndex = index;
    }
    this.scrollFocusedOptionIntoView();
    event.preventDefault();
  }

  handleEndKey(event) {
    if (this.isGroupedSelect) {
      const { groupIndex, optionIndex } = findLastGroupedOption(this.groups);
      this.focusedGroupIndex = groupIndex;
      this.focusedOptionIndex = optionIndex;
    } else {
      const { index } = findLastOption(this.options);
      this.focusedOptionIndex = index;
    }
    this.scrollFocusedOptionIntoView();
    event.preventDefault();
  }

  handleUpArrowKey(event) {
    if (!this.isOpen) {
      this.toggleDropdown();
    } else if (this.hasAvailableOptions) {
      if (this.isGroupedSelect) {
        const { groupIndex, optionIndex } = findPreviousGroupedOption(this.focusedGroupIndex, this.focusedOptionIndex, this.groups);
        this.focusedGroupIndex = groupIndex;
        this.focusedOptionIndex = optionIndex;
      } else {
        const { index } = findPreviousOption(this.focusedOptionIndex, this.options);
        this.focusedOptionIndex = index;
      }
      this.scrollFocusedOptionIntoView();
    }
    event.preventDefault();
  }

  handleDownArrowKey(event) {
    if (!this.isOpen) {
      this.toggleDropdown();
    } else if (this.hasAvailableOptions) {
      if (this.isGroupedSelect) {
        const { groupIndex, optionIndex } = findNextGroupedOption(this.focusedGroupIndex, this.focusedOptionIndex, this.groups);
        this.focusedGroupIndex = groupIndex;
        this.focusedOptionIndex = optionIndex;
      } else {
        const { index } = findNextOption(this.focusedOptionIndex, this.options);
        this.focusedOptionIndex = index;
      }
      this.scrollFocusedOptionIntoView();
    }
    event.preventDefault();
  }

  scrollFocusedOptionIntoView() {
    let option;
    if (this.isGroupedSelect) {
      const group = this.shadowRoot.querySelectorAll('ul')[this.focusedGroupIndex];
      option = group.querySelectorAll('li.option')[this.focusedOptionIndex];
    } else {
      option = this.shadowRoot.querySelectorAll('li.option')[this.focusedOptionIndex];
    }
    option.scrollIntoView({ block: 'nearest' });
  }

  render() {
    const hasLabel = !!this.label || !!this.sublabel;
    return html`
      ${hasLabel ? html`
      <div class="mb-1">
        <label id="aab-select-label">${this.label}</label>
        <span class="sublabel">${this.sublabel}</span>
      </div>` : ''}

      <div class="position-relative">
      ${AabSelectButton({
    hasLabel,
    isOpen: this.isOpen,
    variant: this.variant,
    disabled: this.disabled,
    noBorder: this.noBorder,
    displayValue: this.displayValue(),
    placeholder: this.placeholder,
    hasValue: !!this.value,
    icon: this.selectedOption?.icon,
    onClick: this.toggleDropdown,
  })}
        ${!this.isGroupedSelect
    ? AabSelectDropdown({
      hasLabel,
      focusedOptionIndex: this.focusedOptionIndex,
      value: this.value,
      isOpen: this.isOpen,
      width: this.dropdownWidth,
      align: this.alignDropdown,
      options: this.options,
      onMouseOut: () => { this.focusedOptionIndex = -1; },
      onOptionClick: () => { this.handleOptionSelected(); },
      onOptionMouseOver: (index) => { this.focusedOptionIndex = index; },
    }) : AabSelectGroupedDropdown({
      hasLabel,
      focusedGroupIndex: this.focusedGroupIndex,
      focusedOptionIndex: this.focusedOptionIndex,
      value: this.value,
      isOpen: this.isOpen,
      width: this.dropdownWidth,
      align: this.alignDropdown,
      groups: this.groups,
      onMouseOut: () => { this.focusedOptionIndex = -1; this.focusedGroupIndex = -1; },
      onOptionClick: () => { this.handleOptionSelected(); },
      onOptionMouseOver: (groupIndex, optionIndex) => { this.focusedGroupIndex = groupIndex; this.focusedOptionIndex = optionIndex; },
    })
}
  </div>`;
  }
}
